import clsx from 'clsx'
import { useEffect, useReducer } from 'react'
import styles from './styles.module.scss'
import { ButtonOutline } from '../Button'
import { Checkbox } from '../Checkbox'
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  DragEndEvent,
} from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable'
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers'
import { CSS } from '@dnd-kit/utilities'
import { initialState, reducer } from './reducers'
import { ITransferItemProps, ITransferProps, TLists } from './types'

const TransferItem = ({
  id,
  label,
  checked,
  onChange,
  isDraggable = false,
}: ITransferItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id,
    disabled: !isDraggable,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  // Conditionally apply listeners and attributes based on isDraggable
  const draggableAttributes = isDraggable ? { ...attributes, ...listeners } : {}
  return (
    <div className={styles.sortableItem} ref={setNodeRef} style={style}>
      <Checkbox
        id={id}
        size="sm"
        label={label}
        checked={checked}
        onChange={(e) => {
          e.stopPropagation()
          onChange(e)
        }}
      />
      {isDraggable && (
        <div className={styles.dragHandle} {...draggableAttributes}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 512">
            <path d="M64 128a32 32 0 1 0 0-64 32 32 0 1 0 0 64zm0 160a32 32 0 1 0 0-64 32 32 0 1 0 0 64zM96 416a32 32 0 1 0 -64 0 32 32 0 1 0 64 0zm96-288a32 32 0 1 0 0-64 32 32 0 1 0 0 64zm32 128a32 32 0 1 0 -64 0 32 32 0 1 0 64 0zM192 448a32 32 0 1 0 0-64 32 32 0 1 0 0 64z" />
          </svg>
        </div>
      )}
    </div>
  )
}

export const Transfer = ({
  noHeaders = false,
  active,
  available,
  availableLabel,
  activeLabel,
  hasMoveAllButtons = true,
  onActiveChange,
  onAvailableChange,
  isDraggable = false,
  ...props
}: ITransferProps) => {
  const sensors = useSensors(useSensor(PointerSensor))
  const [state, dispatch] = useReducer(reducer, initialState)
  const activeListName = 'activeList'
  const availableListName = 'availableList'
  const isBothDraggable = typeof isDraggable === 'boolean' && isDraggable
  const isActiveDraggable = typeof isDraggable === 'object' ? isDraggable.active : isBothDraggable
  const isAvailableDraggable =
    typeof isDraggable === 'object' ? isDraggable.available : isBothDraggable

  // Set initial lists
  useEffect(() => {
    dispatch({ type: 'SET_INITIAL_LISTS', available, active })
  }, [available, active])

  // Update external state when internal state changes
  useEffect(() => {
    onActiveChange && onActiveChange(state.activeList)
    onAvailableChange && onAvailableChange(state.availableList)
  }, [state.activeList, state.availableList, onActiveChange, onAvailableChange])

  // Event Handlers
  const handleDragEnd = (event: DragEndEvent, list: TLists) => {
    const { active, over } = event
    if (!isDraggable || !over) return

    let oldIndex = 0
    let newIndex = 0

    if (list === 'activeList') {
      oldIndex = state.activeList.findIndex((item) => item.id === active.id)
      newIndex = state.activeList.findIndex((item) => item.id === over.id)
    } else {
      oldIndex = state.availableList.findIndex((item) => item.id === active.id)
      newIndex = state.availableList.findIndex((item) => item.id === over.id)
    }

    if (newIndex !== oldIndex) {
      dispatch({
        type: 'DRAG_END',
        oldIndex,
        newIndex,
        list,
      })
    }
  }
  const handleAllToActive = () => {
    dispatch({ type: 'MOVE_ALL_TO_ACTIVE' })
  }
  const handleAllToAvailable = () => {
    dispatch({ type: 'MOVE_ALL_TO_AVAILABLE' })
  }
  const handleAvailableToActive = () => {
    dispatch({ type: 'MOVE_AVAILABLE_TO_ACTIVE' })
  }
  const handleActiveToAvailable = () => {
    dispatch({ type: 'MOVE_ACTIVE_TO_AVAILABLE' })
  }
  const handleSelectAllActive = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: 'SELECT_ALL_ACTIVE', event })
  }
  const handleSelectAllAvailable = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: 'SELECT_ALL_AVAILABLE', event })
  }

  const handleToggleCheck = (
    event: React.ChangeEvent<HTMLInputElement>,
    id: string,
    isChecked: boolean,
    currentList: TLists,
  ) => {
    event.stopPropagation()
    dispatch({ type: 'TOGGLE_CHECK', id: id, isChecked: isChecked, list: currentList })
  }
  return (
    <div className={styles.transferHolder} {...props}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        onDragEnd={(e) => handleDragEnd(e, 'activeList')}
      >
        <SortableContext
          items={state.activeList.map((item) => item.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className={clsx(styles.transferLeft)}>
            <>
              {!noHeaders && (
                <div className={clsx(styles.transferHeader)}>
                  <Checkbox
                    id="selectAllSelected"
                    size="sm"
                    label={`${state.checkedActive.length} Selected`}
                    className={styles.headerCheckbox}
                    checked={
                      state.checkedActive.length === state.activeList.length &&
                      state.activeList.length > 0
                    }
                    onChange={(e) => handleSelectAllActive(e)}
                  />
                  <span className={styles.activeLabel}>
                    {state.activeList.length} {activeLabel}
                  </span>
                </div>
              )}
              <div className={clsx(styles.transferList)} id="active-list">
                {state.activeList.length === 0 ? (
                  <div className={styles.emptyList}>0 {activeLabel}</div>
                ) : (
                  state.activeList.map((option) => (
                    <TransferItem
                      key={option.id}
                      id={option.id}
                      label={option.label}
                      checked={state.checkedActive.includes(option.id)}
                      onChange={(e) =>
                        handleToggleCheck(e, option.id, e.target.checked, activeListName)
                      }
                      isDraggable={isActiveDraggable}
                    />
                  ))
                )}
              </div>
            </>
          </div>
        </SortableContext>
      </DndContext>
      <div className={clsx(styles.transferButtons)}>
        {hasMoveAllButtons && (
          <ButtonOutline
            size="sm"
            onClick={handleAllToAvailable}
            disabled={state.activeList.length === 0}
            className={clsx(styles.transferButton, styles.moveAllButton)}
          >
            {/* Double Right */}
            <svg
              className={styles.vertical}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 512 512"
            >
              <path d="M470.6 278.6c12.5-12.5 12.5-32.8 0-45.3l-192-192c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L402.7 256 233.4 425.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l192-192zm-384 192l192-192c12.5-12.5 12.5-32.8 0-45.3l-192-192c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L210.7 256 41.4 425.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0z" />
            </svg>
            <svg
              className={styles.horizontal}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 512 512"
            >
              <path d="M278.6 470.6c-12.5 12.5-32.8 12.5-45.3 0l-192-192c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L256 402.7 425.4 233.4c12.5-12.5 32.8-12.5 45.3 0s12.5 32.8 0 45.3l-192 192zm192-384l-192 192c-12.5 12.5-32.8 12.5-45.3 0l-192-192c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L256 210.7 425.4 41.4c12.5-12.5 32.8-12.5 45.3 0s12.5 32.8 0 45.3z" />
            </svg>
          </ButtonOutline>
        )}
        <ButtonOutline
          size="sm"
          onClick={handleActiveToAvailable}
          disabled={state.checkedActive.length === 0}
          className={clsx(styles.transferButton)}
        >
          {/* Right */}
          <svg className={styles.vertical} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
            <path d="M310.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-192 192c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L242.7 256 73.4 86.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l192 192z" />
          </svg>
          <svg
            className={styles.horizontal}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 512 512"
          >
            <path d="M233.4 406.6c12.5 12.5 32.8 12.5 45.3 0l192-192c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L256 338.7 86.6 169.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l192 192z" />
          </svg>
        </ButtonOutline>
        <ButtonOutline
          size="sm"
          onClick={handleAvailableToActive}
          disabled={state.checkedAvailable.length === 0}
          className={clsx(styles.transferButton)}
        >
          {/* Left */}
          <svg className={styles.vertical} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
            <path d="M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l192 192c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L77.3 256 246.6 86.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-192 192z" />
          </svg>
          <svg
            className={styles.horizontal}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 512 512"
          >
            <path d="M233.4 105.4c12.5-12.5 32.8-12.5 45.3 0l192 192c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L256 173.3 86.6 342.6c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3l192-192z" />
          </svg>
        </ButtonOutline>
        {/* Double Left */}
        {hasMoveAllButtons && (
          <ButtonOutline
            size="sm"
            onClick={handleAllToActive}
            disabled={state.availableList.length === 0}
            className={clsx(styles.transferButton, styles.moveAllButton)}
          >
            <svg
              className={styles.vertical}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 512 512"
            >
              <path d="M41.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l192 192c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.3 256 278.6 86.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-192 192zm384-192l-192 192c-12.5 12.5-12.5 32.8 0 45.3l192 192c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L301.3 256 470.6 86.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0z" />
            </svg>
            <svg
              className={styles.horizontal}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 512 512"
            >
              <path d="M278.6 41.4c-12.5-12.5-32.8-12.5-45.3 0l-192 192c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L256 109.3 425.4 278.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3l-192-192zm192 384l-192-192c-12.5-12.5-32.8-12.5-45.3 0l-192 192c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L256 301.3 425.4 470.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3z" />
            </svg>
          </ButtonOutline>
        )}
      </div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        onDragEnd={(e) => handleDragEnd(e, 'availableList')}
      >
        <SortableContext
          items={state.availableList.map((item) => item.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className={clsx(styles.transferRight)}>
            <>
              {!noHeaders && (
                <div className={clsx(styles.transferHeader)}>
                  <Checkbox
                    id="selectAllAvailable"
                    className={styles.headerCheckbox}
                    size="sm"
                    label={`${state.checkedAvailable.length} Selected`}
                    checked={
                      state.checkedAvailable.length === state.availableList.length &&
                      state.availableList.length > 0
                    }
                    onChange={(e) => handleSelectAllAvailable(e)}
                  />
                  <span className={styles.availableLabel}>
                    {String(state.availableList.length)} {availableLabel}
                  </span>
                </div>
              )}
              <div className={clsx(styles.transferList)}>
                {state.availableList.length === 0 ? (
                  <div className={styles.emptyList}>0 {availableLabel}</div>
                ) : (
                  state.availableList.map((option) => (
                    <TransferItem
                      key={option.id}
                      id={option.id}
                      label={option.label}
                      checked={state.checkedAvailable.includes(option.id)}
                      onChange={(e) =>
                        handleToggleCheck(e, option.id, e.target.checked, availableListName)
                      }
                      isDraggable={isAvailableDraggable}
                    />
                  ))
                )}
              </div>
            </>
          </div>
        </SortableContext>
      </DndContext>
    </div>
  )
}

Transfer.displayName = 'Transfer'
